<form [formGroup]="zoneForm" class="atp">
  <header class="atp-head">
    <button type="button" class="atp-close" (click)="cancel()" aria-label="Close">
      <i class="bx bx-x" aria-hidden="true"></i>
    </button>
    <h2 class="atp-title">
      @if (zone) {
        Edit {{ zone.zone_name }}
      } @else {
        Add New Zone
      }
    </h2>
    <div class="atp-actions">
      <button type="button" class="atp-cancel" (click)="cancel()">Cancel</button>
      <button
        type="button"
        class="atp-save"
        [disabled]="zoneForm.invalid || isDuplicate"
        (click)="save()"
      >
        Save Zone
      </button>
    </div>
  </header>

  <div class="atp-body">
    <!-- Basic information -->
    <section class="atp-card">
      <header class="atp-card-head">
        <span class="atp-card-icon"><i class="bx bx-file" aria-hidden="true"></i></span>
        <div class="atp-card-headings">
          <h3 class="atp-card-title">Basic Information</h3>
          <p class="atp-card-sub">Provide basic details to define this zone.</p>
        </div>
      </header>

      <label class="atp-label" for="zoneName">Zone Name *</label>
      <input
        id="zoneName"
        class="atp-input"
        type="text"
        autocomplete="off"
        placeholder="e.g. Main Dining"
        formControlName="zone_name"
        (input)="checkDuplicate()"
        [class.input-error]="
          isDuplicate || (zoneForm.get('zone_name')?.invalid && zoneForm.get('zone_name')?.touched)
        "
      />
      @if (zoneForm.get('zone_name')?.touched && zoneForm.get('zone_name')?.errors?.['required']) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Zone name cannot be empty.</p>
        </div>
      }
      @if (zoneForm.get('zone_name')?.touched && zoneForm.get('zone_name')?.errors?.['minlength']) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Zone name must be at least 3 characters.</p>
        </div>
      }
      @if (isDuplicate) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Zone name is already existing.</p>
        </div>
      }

      <label class="atp-label atp-label--spaced" for="zoneCapacity">Max Capacity *</label>
      <input
        id="zoneCapacity"
        class="atp-input"
        type="number"
        min="1"
        max="500"
        placeholder="e.g. 90"
        formControlName="capacity"
        [class.input-error]="zoneForm.get('capacity')?.invalid && zoneForm.get('capacity')?.touched"
      />
      <p class="atp-help">Maximum number of guests this zone can accommodate</p>
      @if (zoneForm.get('capacity')?.touched && zoneForm.get('capacity')?.invalid) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Capacity must be between 1 and 500.</p>
        </div>
      }
    </section>

    <!-- Location & access -->
    <section class="atp-card">
      <header class="atp-card-head">
        <span class="atp-card-icon"><i class="bx bx-map-pin" aria-hidden="true"></i></span>
        <div class="atp-card-headings">
          <h3 class="atp-card-title">Location &amp; Access</h3>
          <p class="atp-card-sub">Specify layout and physical accessibility parameters.</p>
        </div>
      </header>

      <label class="atp-label" for="zoneFloor">Floor Number</label>
      <input
        id="zoneFloor"
        class="atp-input"
        type="number"
        min="0"
        max="10"
        placeholder="e.g. 1"
        formControlName="floor"
        [class.input-error]="zoneForm.get('floor')?.invalid && zoneForm.get('floor')?.touched"
      />
      <p class="atp-help">Which floor is this zone located on</p>
      @if (zoneForm.get('floor')?.touched && zoneForm.get('floor')?.invalid) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>Floor must be between 0 and 10.</p>
        </div>
      }

      <div class="atp-setting-row atp-setting-row--spaced">
        <div>
          <p class="atp-setting-title">Wheelchair Accessible</p>
          <p class="atp-setting-sub">Enable if this zone is accessible for wheelchair users</p>
        </div>
        <div class="form-check form-switch atp-switch">
          <input
            class="form-check-input"
            type="checkbox"
            role="switch"
            id="wheelchair"
            formControlName="wheelchair_accessibility"
            aria-label="Wheelchair accessible"
          />
        </div>
      </div>
    </section>

    <!-- Zone types -->
    <section class="atp-card">
      <header class="atp-card-head">
        <span class="atp-card-icon"><i class="bx bx-layer" aria-hidden="true"></i></span>
        <div class="atp-card-headings">
          <h3 class="atp-card-title">Zone Types</h3>
          <p class="atp-card-sub">Categorize the zone's operational or atmosphere target.</p>
        </div>
      </header>

      <span class="atp-label">Assign Types</span>
      <p class="atp-help atp-help--tight">Select up to {{ maxTypes }} types for this zone</p>

      @if (typeOptions.length) {
        <div class="atp-chips zf-chips">
          @for (option of typeOptions; track option) {
            <button
              type="button"
              class="atp-chip atp-chip--wide"
              [class.atp-chip--active]="isAssigned(option)"
              [disabled]="!isAssigned(option) && atTypeLimit"
              (click)="toggleType(option)"
            >
              {{ option }}
            </button>
          }
        </div>
      }

      <!-- Types that are not offered as chips still need a way in and out. -->
      @if (customTypes.length) {
        <div class="atp-chips zf-chips">
          @for (custom of customTypes; track custom) {
            <span class="atp-chip atp-chip--wide atp-chip--active zf-custom">
              {{ custom }}
              <button
                type="button"
                class="zf-remove"
                (click)="removeType(custom)"
                [attr.aria-label]="'Remove ' + custom"
              >
                <i class="bx bx-x" aria-hidden="true"></i>
              </button>
            </span>
          }
        </div>
      }

      <div class="zf-add-row">
        <input
          class="atp-input"
          type="text"
          placeholder="Add another type…"
          [(ngModel)]="customType"
          [ngModelOptions]="{ standalone: true }"
          (keyup.enter)="addCustomType()"
        />
        <button
          type="button"
          class="zf-add-btn"
          [disabled]="!customType.trim() || atTypeLimit"
          (click)="addCustomType()"
        >
          Add
        </button>
      </div>

      @if (typeError) {
        <div class="d-flex mt-2 errorText gap-2">
          <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
          <p>{{ typeError }}</p>
        </div>
      }

      <p class="zf-count">{{ assignedTypes.length }} / {{ maxTypes }} selected</p>
    </section>
  </div>
</form>
